import type { DocumentSaver } from "../../documentSaver.ts"
import { type Identified, rebase } from "./drawingsSync.ts"

// One symbol's drawings, shared by every chart that shows the symbol (any pane,
// any timeframe), as in TradingView. The hub holds the one current list per
// symbol; a pane publishes its edits and gets every other pane's, within the
// same task (so a drag moves the drawing in every chart as it happens).
//
// What's on the server is never overwritten by a list that wasn't built on it:
// edits made before the server's copy loaded are merged into it once it does,
// and a symbol whose copy can't be read is never saved (it retries instead).

/** unsaved: read, but the server refused a save (the drawings are kept here and saved with the next change). */
export type DrawingsStatus = "loading" | "ready" | "unavailable" | "unsaved"

export interface DrawingsSnapshot<D> {
  readonly status: DrawingsStatus
  readonly version: number
  readonly drawings: ReadonlyArray<D>
}

export interface DrawingsSubscription<D> {
  /** The symbol's current drawings and status. */
  readonly current: () => DrawingsSnapshot<D>
  /** This pane's list went from `before` to `after`; returns the merged list. */
  readonly publish: (before: ReadonlyArray<D>, after: ReadonlyArray<D>) => DrawingsSnapshot<D>
  readonly close: () => void
}

export interface DrawingsHub<D> {
  /** Follows a symbol's drawings: `onSnapshot` gets each change made elsewhere. */
  readonly open: (key: string, onSnapshot: (snapshot: DrawingsSnapshot<D>) => void) => DrawingsSubscription<D>
  /** Sends pending saves now (before signing out). */
  readonly flush: () => Promise<void>
  /** What the saver reports: a save refused for good, or saved again after one was. */
  readonly saveStatus: (key: string, status: "saved" | "failed") => void
  /** Forgets everything (another user): loads in flight are ignored, retries stop. */
  readonly reset: () => void
}

export interface HubDeps<D> {
  /** The stored document, null when there is none. Rejects when it can't be read. */
  readonly load: (key: string) => Promise<string | null>
  /** The drawings in a stored document; null when it isn't a valid one. */
  readonly parse: (json: string | null) => ReadonlyArray<D> | null
  readonly saver: DocumentSaver
  /** Runs `deliver` soon, once per batch of changes (a microtask in the app). */
  readonly schedule: (deliver: () => void) => void
  readonly setTimer?: (run: () => void, ms: number) => unknown
  readonly clearTimer?: (timer: unknown) => void
}

const RETRY_MS = [2_000, 10_000, 30_000]

/** The server's copy was read: edits can be saved. */
const readable = (entry: { readonly status: DrawingsStatus }) => entry.status === "ready" || entry.status === "unsaved"

interface Subscriber<D> {
  readonly onSnapshot: (snapshot: DrawingsSnapshot<D>) => void
  /** The last version this pane has. */
  seen: number
}

interface Entry<D> {
  status: DrawingsStatus
  version: number
  drawings: ReadonlyArray<D>
  readonly subscribers: Set<Subscriber<D>>
  /** A load in flight (ignored when it doesn't match: superseded or reset). */
  loading: number | null
  /** Edits made before the server's copy was read: saved once they're merged into it. */
  unsaved: boolean
  retry: unknown
  attempt: number
}

export const createDrawingsHub = <D extends Identified>(deps: HubDeps<D>): DrawingsHub<D> => {
  const setTimer = deps.setTimer ?? ((run, ms) => setTimeout(run, ms))
  const clearTimer = deps.clearTimer ?? ((timer) => clearTimeout(timer as ReturnType<typeof setTimeout>))
  const EMPTY: ReadonlyArray<D> = []
  let entries = new Map<string, Entry<D>>()
  let nextLoad = 1
  const dirty = new Set<Entry<D>>()
  let delivering = false

  const snapshotOf = (entry: Entry<D>): DrawingsSnapshot<D> => ({
    status: entry.status,
    version: entry.version,
    drawings: entry.drawings
  })

  const deliver = () => {
    delivering = false
    const changed = [...dirty]
    dirty.clear()
    for (const entry of changed) {
      const snapshot = snapshotOf(entry)
      for (const subscriber of entry.subscribers) {
        if (subscriber.seen >= snapshot.version) continue
        subscriber.seen = snapshot.version
        subscriber.onSnapshot(snapshot)
      }
    }
  }

  const changed = (entry: Entry<D>) => {
    entry.version++
    dirty.add(entry)
    if (delivering) return
    delivering = true
    deps.schedule(deliver)
  }

  const save = (key: string, entry: Entry<D>) => deps.saver.schedule(key, () => JSON.stringify(entry.drawings))

  const load = (key: string, entry: Entry<D>) => {
    const token = nextLoad++
    entry.loading = token
    const current = () => entries.get(key) === entry && entry.loading === token
    deps.load(key).then(
      (json) => {
        if (!current()) return
        entry.loading = null
        const stored = deps.parse(json)
        if (stored === null) return failed(key, entry)
        // Edits made while loading are kept on top of the server's copy.
        entry.drawings = rebase(stored, EMPTY, entry.drawings)
        entry.status = "ready"
        entry.attempt = 0
        // A retry left from an earlier failure must not load it again over newer edits.
        clearTimer(entry.retry)
        entry.retry = undefined
        if (entry.unsaved) save(key, entry)
        entry.unsaved = false
        changed(entry)
      },
      () => {
        if (!current()) return
        entry.loading = null
        failed(key, entry)
      }
    )
  }

  const failed = (key: string, entry: Entry<D>) => {
    if (entry.status !== "unavailable") {
      entry.status = "unavailable"
      changed(entry)
    }
    // Retries stop while no chart shows the symbol; the next open() tries again.
    if (entry.subscribers.size === 0) return
    clearTimer(entry.retry)
    entry.retry = setTimer(() => {
      entry.retry = undefined
      if (entries.get(key) === entry && !readable(entry) && entry.loading === null && entry.subscribers.size > 0) {
        load(key, entry)
      }
    }, RETRY_MS[Math.min(entry.attempt++, RETRY_MS.length - 1)]!)
  }

  return {
    open: (key, onSnapshot) => {
      let entry = entries.get(key)
      if (entry === undefined) {
        entry = {
          status: "loading",
          version: 0,
          drawings: EMPTY,
          subscribers: new Set(),
          loading: null,
          unsaved: false,
          retry: undefined,
          attempt: 0
        }
        entries.set(key, entry)
      }
      const subscriber: Subscriber<D> = { onSnapshot, seen: entry.version }
      entry.subscribers.add(subscriber)
      if (!readable(entry) && entry.loading === null) load(key, entry)
      const own = entry
      return {
        current: () => snapshotOf(own),
        publish: (before, after) => {
          const next = rebase(own.drawings, before, after)
          if (next === own.drawings) return snapshotOf(own)
          own.drawings = next
          changed(own)
          // The publisher already shows its own list; a merged one is delivered back to it.
          if (next === after) subscriber.seen = own.version
          if (readable(own)) save(key, own)
          else own.unsaved = true
          return snapshotOf(own)
        },
        close: () => {
          own.subscribers.delete(subscriber)
          if (own.subscribers.size === 0) {
            clearTimer(own.retry)
            own.retry = undefined
          }
        }
      }
    },
    flush: () => deps.saver.flush(),
    saveStatus: (key, status) => {
      const entry = entries.get(key)
      if (entry === undefined) return
      const next: DrawingsStatus = status === "failed" && entry.status === "ready"
        ? "unsaved"
        : status === "saved" && entry.status === "unsaved"
        ? "ready"
        : entry.status
      if (next === entry.status) return
      entry.status = next
      changed(entry)
    },
    reset: () => {
      for (const entry of entries.values()) clearTimer(entry.retry)
      entries = new Map()
      dirty.clear()
      deps.saver.discard()
    }
  }
}
